/**
 * numero.ts — preview (tentative) report-number builder for the frontend.
 *
 * Réplica EXACTA del formato del backend (apps/api/src/domain/services/numero.ts
 * + sigla.ts + iniciales.ts). Se duplica acá a propósito: permite MOSTRAR el código
 * de un informe sin número definitivo (borrador / pendiente de sincronizar) sin
 * pegarle al server.
 *
 * IMPORTANTE: es una PREDICCIÓN. El correlativo real lo acuña el server con un
 * contador atómico por técnico al crear el informe. Coincide con el número final
 * salvo que haya varios borradores sin sincronizar del mismo técnico, o que el
 * contador del server avance entremedio.
 *
 * Formato: {TIPO}{YY}{INICIALES_TECNICO}{CORRELATIVO3}-{SIGLA_CLIENTE}
 *   ej. IE26DT021-KMMP
 */
import type { TipoInforme } from "../types.js";
import { deriveSigla } from "./sigla.js";

/** Primer carácter alfabético de una cadena, en mayúsculas; "X" si no hay ninguno. */
function primeraLetra(palabra: string): string {
  const match = palabra.match(/[a-zA-ZÀ-ÿ]/);
  return match ? match[0].toUpperCase() : "X";
}

/**
 * Iniciales del técnico. Réplica de apps/api/src/domain/services/iniciales.ts.
 *   - ≥2 palabras → primera letra de la primera + primera letra de la última. "Diego Tafur" → "DT".
 *   - 1 palabra   → sus dos primeras letras. "Diego" → "DI".
 */
export function deriveIniciales(nombre: string): string {
  const palabras = nombre
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0);

  const primeraPalabra = palabras[0];
  if (primeraPalabra === undefined) {
    return "XX";
  }

  if (palabras.length === 1) {
    const letras = primeraPalabra.match(/[a-zA-ZÀ-ÿ]/g) ?? [];
    const primera = letras[0]?.toUpperCase() ?? "X";
    const segunda = letras[1]?.toUpperCase() ?? "X";
    return `${primera}${segunda}`;
  }

  const ultimaPalabra = palabras[palabras.length - 1] ?? primeraPalabra;
  return `${primeraLetra(primeraPalabra)}${primeraLetra(ultimaPalabra)}`;
}

export interface PreviewNumeroParts {
  tipo: TipoInforme;
  anio: number; // año completo, ej. 2026
  nombreTecnico: string;
  correlativo: number; // predicción: cantidad de informes del técnico + 1
  clienteNombre: string;
}

/** Arma el código tentativo del informe. Mismo formato que buildNumero del backend. */
export function previewNumero(parts: PreviewNumeroParts): string {
  const yy = String(parts.anio % 100).padStart(2, "0");
  const correlativo3 = String(parts.correlativo).padStart(3, "0");
  const iniciales = deriveIniciales(parts.nombreTecnico);
  const sigla = deriveSigla(parts.clienteNombre);
  return `${parts.tipo}${yy}${iniciales}${correlativo3}-${sigla}`;
}
